'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function IdealWeightCalculator() {
  const t = useTranslations("tools.idealWeightCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentUnit, setCurrentUnit] = useState<'imperial' | 'metric'>('imperial');
  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    heightFeet: 5,
    heightInches: 10,
    heightCm: 178,
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const getHeightInInches = () => {
    if (currentUnit === 'imperial') {
      return inputs.heightFeet * 12 + inputs.heightInches;
    } else {
      return inputs.heightCm / 2.54;
    }
  };

  const calculateRobinson = (heightInches: number, isMale: boolean) => {
    if (heightInches <= 60) return isMale ? 52 : 49;
    const base = isMale ? 52 : 49;
    const increment = isMale ? 1.9 : 1.7;
    return base + increment * (heightInches - 60);
  };

  const calculateMiller = (heightInches: number, isMale: boolean) => {
    if (heightInches <= 60) return isMale ? 56.2 : 53.1;
    const base = isMale ? 56.2 : 53.1;
    const increment = isMale ? 1.41 : 1.36;
    return base + increment * (heightInches - 60);
  };

  const calculateDevine = (heightInches: number, isMale: boolean) => {
    if (heightInches <= 60) return isMale ? 50 : 45.5;
    const base = isMale ? 50 : 45.5;
    const increment = 2.3;
    return base + increment * (heightInches - 60);
  };

  const calculateHamwi = (heightInches: number, isMale: boolean) => {
    if (heightInches <= 60) return isMale ? 48 : 45.5;
    const base = isMale ? 48 : 45.5;
    const increment = isMale ? 2.7 : 2.2;
    return base + increment * (heightInches - 60);
  };

  const calculateBMIRange = (heightInches: number) => {
    const heightMeters = heightInches * 0.0254;
    const minWeight = 18.5 * heightMeters * heightMeters;
    const maxWeight = 24.9 * heightMeters * heightMeters;
    return { min: minWeight, max: maxWeight };
  };

  const kgToLbs = (kg: number) => kg * 2.20462;

  const formatWeight = (kg: number) => {
    if (currentUnit === 'imperial') {
      return kgToLbs(kg).toFixed(1) + ' lbs';
    } else {
      return kg.toFixed(1) + ' kg';
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (inputs.age < 2 || inputs.age > 80) {
      setError(t('errorAge'));
      return;
    }

    const heightInches = getHeightInInches();

    if (heightInches < 24 || heightInches > 96) {
      setError(t('errorHeight'));
      return;
    }

    const isMale = inputs.gender === 'male';
    const robinson = calculateRobinson(heightInches, isMale);
    const miller = calculateMiller(heightInches, isMale);
    const devine = calculateDevine(heightInches, isMale);
    const hamwi = calculateHamwi(heightInches, isMale);
    const bmiRange = calculateBMIRange(heightInches);

    setResults({
      robinson,
      miller,
      devine,
      hamwi,
      bmiRange,
    });

    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const unitTabsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    background: '#f1f5f9',
    padding: '0.5rem',
    borderRadius: '0.75rem',
    flexDirection: screenSize === 'mobile' ? 'column' : 'row',
  };

  const unitTabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '0.75rem 1rem',
    background: isActive ? 'white' : 'transparent',
    border: 'none',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 600,
    color: isActive ? '#2563eb' : '#64748b',
    boxShadow: isActive ? '0 2px 8px rgba(37, 99, 235, 0.2)' : 'none',
    transition: 'all 0.3s ease',
  });

  const formGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
    marginBottom: '1.5rem',
  };

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    {},
    { padding: '0.875rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const selectStyle = { ...inputStyle };

  const heightInputsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
  };

  const inputSuffixContainerStyle: React.CSSProperties = {
    position: 'relative',
    flex: 1,
  };

  const inputSuffixStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontSize: '0.9rem',
    pointerEvents: 'none',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '1rem',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const errorMessageStyle: React.CSSProperties = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    borderLeft: '4px solid #dc2626',
  };

  const resultsStyle = getResponsiveStyle(
    {
      padding: '2rem',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
    },
    {},
    {},
    {}
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#1e293b', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
    { fontSize: '1.4rem' },
    { fontSize: '1.45rem' },
    { fontSize: '1.5rem' }
  );

  const resultsTableContainerStyle: React.CSSProperties = {
    width: '100%',
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const thStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '1rem',
      textAlign: 'left' as const,
      fontWeight: 600,
    },
    { fontSize: '0.9rem', padding: '0.75rem 0.5rem' },
    { fontSize: '0.95rem', padding: '0.875rem 0.75rem' },
    { fontSize: '1rem', padding: '1rem' }
  );

  const tdStyle = getResponsiveStyle(
    {
      padding: '1rem',
      borderBottom: '1px solid #e2e8f0',
    },
    { fontSize: '0.9rem', padding: '0.75rem 0.5rem' },
    { fontSize: '0.95rem', padding: '0.875rem 0.75rem' },
    { fontSize: '1rem', padding: '1rem' }
  );

  const formulaNameStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#334155',
  };

  const weightValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const weightRangeStyle = getResponsiveStyle(
    { color: '#059669', fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const bmiHighlightStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem', paddingLeft: '1.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const liStyle: React.CSSProperties = {
    marginBottom: '0.5rem',
  };

  const strongStyle: React.CSSProperties = {
    color: colors.textPrimary,
    fontWeight: 600,
  };

  const linkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>⚖️ {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={unitTabsStyle}>
              <button
                type="button"
                style={unitTabStyle(currentUnit === 'imperial')}
                onClick={() => setCurrentUnit('imperial')}
              >
                {t('unitImperial')}
              </button>
              <button
                type="button"
                style={unitTabStyle(currentUnit === 'metric')}
                onClick={() => setCurrentUnit('metric')}
              >
                {t('unitMetric')}
              </button>
            </div>

            {error && (
              <div style={errorMessageStyle}>{error}</div>
            )}

            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="age">{t('labelAge')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="age"
                  min="2"
                  max="80"
                  value={inputs.age}
                  onChange={(e) => setInputs({ ...inputs, age: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="gender">{t('labelGender')}</label>
                <select
                  style={selectStyle}
                  id="gender"
                  value={inputs.gender}
                  onChange={(e) => setInputs({ ...inputs, gender: e.target.value as 'male' | 'female' })}
                  required
                >
                  <option value="male">{t('genderMale')}</option>
                  <option value="female">{t('genderFemale')}</option>
                </select>
              </div>
            </div>

            {currentUnit === 'imperial' ? (
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('labelHeight')}</label>
                <div style={heightInputsStyle}>
                  <div style={inputSuffixContainerStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="height-feet"
                      min="0"
                      max="8"
                      value={inputs.heightFeet}
                      onChange={(e) => setInputs({ ...inputs, heightFeet: parseInt(e.target.value) || 0 })}
                      required
                    />
                    <span style={inputSuffixStyle}>{t('unitFeet')}</span>
                  </div>
                  <div style={inputSuffixContainerStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="height-inches"
                      min="0"
                      max="11"
                      value={inputs.heightInches}
                      onChange={(e) => setInputs({ ...inputs, heightInches: parseInt(e.target.value) || 0 })}
                      required
                    />
                    <span style={inputSuffixStyle}>{t('unitInches')}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('labelHeight')}</label>
                <div style={inputSuffixContainerStyle}>
                  <input
                    style={inputStyle}
                    type="number"
                    id="height-cm"
                    min="50"
                    max="250"
                    step="0.1"
                    value={inputs.heightCm}
                    onChange={(e) => setInputs({ ...inputs, heightCm: parseFloat(e.target.value) || 0 })}
                    required
                  />
                  <span style={inputSuffixStyle}>{t('unitCm')}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>
              <div style={resultsTableContainerStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('tableHeaderFormula')}</th>
                      <th style={thStyle}>{t('tableHeaderWeight')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={tdStyle}>
                        <span style={formulaNameStyle}>{t('formulaRobinson')}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={weightValueStyle}>{formatWeight(results.robinson)}</span>
                      </td>
                    </tr>
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={tdStyle}>
                        <span style={formulaNameStyle}>{t('formulaMiller')}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={weightValueStyle}>{formatWeight(results.miller)}</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>
                        <span style={formulaNameStyle}>{t('formulaDevine')}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={weightValueStyle}>{formatWeight(results.devine)}</span>
                      </td>
                    </tr>
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={tdStyle}>
                        <span style={formulaNameStyle}>{t('formulaHamwi')}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={weightValueStyle}>{formatWeight(results.hamwi)}</span>
                      </td>
                    </tr>
                    <tr style={bmiHighlightStyle}>
                      <td style={{ ...tdStyle, borderBottom: 'none' }}>
                        <span style={formulaNameStyle}>{t('formulaBMI')}</span>
                      </td>
                      <td style={{ ...tdStyle, borderBottom: 'none' }}>
                        <span style={weightRangeStyle}>
                          {formatWeight(results.bmiRange.min)} - {formatWeight(results.bmiRange.max)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={liStyle}>{t('howToUseStep1')}</li>
          <li style={liStyle}>{t('howToUseStep2')}</li>
          <li style={liStyle}>{t('howToUseStep3')}</li>
          <li style={liStyle}>{t('howToUseStep4')}</li>
          <li style={liStyle}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsFormulaBold')}</strong> {t('insightsFormulaText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsBMIBold')}</strong> {t('insightsBMIText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsComparisonBold')}</strong> {t('insightsComparisonText')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <ul style={listStyle}>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsScreeningBold')}</strong> {t('insightsScreeningText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsGoalBold')}</strong> {t('insightsGoalText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsClinicalBold')}</strong> {t('insightsClinicalText')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <ul style={listStyle}>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsHistoryBold')}</strong> {t('insightsHistoryText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsBMIRangeBold')}</strong> {t('insightsBMIRangeText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsHealthRisksBold')}</strong> {t('insightsHealthRisksText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('insightsWaistBold')}</strong> {t('insightsWaistText')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>
        <ul style={listStyle}>
          <li style={liStyle}>{t('insightsLimitation1')}</li>
          <li style={liStyle}>{t('insightsLimitation2')}</li>
          <li style={liStyle}>{t('insightsLimitation3')}</li>
          <li style={liStyle}>{t('insightsLimitation4')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle5')}</h3>
        <ul style={listStyle}>
          <li style={liStyle}>{t('insightsBestPractice1')}</li>
          <li style={liStyle}>{t('insightsBestPractice2')}</li>
          <li style={liStyle}>{t('insightsBestPractice3')}</li>
          <li style={liStyle}>{t('insightsBestPractice4')}</li>
          <li style={liStyle}>{t('insightsBestPractice5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={liStyle}><strong style={strongStyle}>{t('detailsRobinsonBold')}</strong> {t('detailsRobinsonText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('detailsMillerBold')}</strong> {t('detailsMillerText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('detailsDevineBold')}</strong> {t('detailsDevineText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('detailsHamwiBold')}</strong> {t('detailsHamwiText')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
        <ul style={listStyle}>
          <li style={liStyle}><strong style={strongStyle}>{t('detailsAboveBold')}</strong> {t('detailsAboveText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('detailsBelowBold')}</strong> {t('detailsBelowText')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('detailsWithinBold')}</strong> {t('detailsWithinText')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph5')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faqQuestion1')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer1')}</p>

        <h3 style={h3Style}>{t('faqQuestion2')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer2')}</p>

        <h3 style={h3Style}>{t('faqQuestion3')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer3')}</p>

        <h3 style={h3Style}>{t('faqQuestion4')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer4')}</p>

        <h3 style={h3Style}>{t('faqQuestion5')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer5')}</p>

        <h3 style={h3Style}>{t('faqQuestion6')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer6')}</p>

        <h3 style={h3Style}>{t('faqQuestion7')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer7')}</p>

        <h3 style={h3Style}>{t('faqQuestion8')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer8')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={liStyle}>
            <a href="https://www.calculator.net/ideal-weight-calculator.html" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference1')}
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://www.omnicalculator.com/health/ideal-weight" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference2')}
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://www.nhlbi.nih.gov/health/educational/lose_wt/BMI/bmicalc.htm" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference3')}
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://www.cdc.gov/healthyweight/assessing/bmi/adult_bmi/index.html" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference4')}
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://www.niddk.nih.gov/health-information/weight-management/adult-overweight-obesity/definition-facts" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}